'use client'

import { useAppRouter } from '@/components/Link'

import isEqual from 'lodash/isEqual'
import { usePathname, useSearchParams } from 'next/navigation'
import qs from 'query-string'
import type { ParsedUrlQueryInput } from 'querystring'
import { useCallback, useEffect, useMemo, useState } from 'react'

const parseQueryString = (queryObj: ParsedUrlQueryInput) => qs.parse(qs.stringify(queryObj))

const mergeQuery = (
  current: ParsedUrlQueryInput,
  updates: ParsedUrlQueryInput,
): ParsedUrlQueryInput => {
  const merged = { ...current }

  Object.entries(updates).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      delete (merged as Record<string, unknown>)[key]
      return
    }
    if (Array.isArray(value) && value.length === 0) {
      delete (merged as Record<string, unknown>)[key]
      return
    }
    ;(merged as Record<string, unknown>)[key] = value
  })

  return merged
}

const useUrlSearchFilters = (setQueryParams = true) => {
  const router = useAppRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const initialFilters = useMemo(() => {
    const parsed = qs.parse(searchParams?.toString() ?? '') as ParsedUrlQueryInput
    return parseQueryString(parsed)
  }, [searchParams])

  const [filters, setFilters] = useState<ParsedUrlQueryInput>(initialFilters as ParsedUrlQueryInput)

  useEffect(() => {
    setFilters(initialFilters as ParsedUrlQueryInput)
  }, [initialFilters])

  const handleSetFilters = useCallback(
    (param: ParsedUrlQueryInput, options?: { silent?: boolean }) => {
      const current = qs.parse(searchParams?.toString() ?? '') as ParsedUrlQueryInput
      const merged = parseQueryString(mergeQuery(current, param))

      if (isEqual(parseQueryString(current), merged)) return

      setFilters((prev) => parseQueryString(mergeQuery(prev, param)) as ParsedUrlQueryInput)
      if (!setQueryParams) return

      const queryString = qs.stringify(merged, {
        skipNull: true,
        skipEmptyString: true,
      })

      const target = queryString ? `${pathname}?${queryString}` : pathname
      if (!target) return

      if (options?.silent && typeof window !== 'undefined') {
        window.history.replaceState(window.history.state, '', target)
        return
      }

      router.replace(target, { scroll: false })
    },
    [router, pathname, searchParams, setQueryParams],
  )

  return [filters, handleSetFilters] as const
}

export default useUrlSearchFilters
